import React from 'react'
import { withRouter, RouteComponentProps, useHistory } from 'react-router-dom'
import truncate from 'lodash.truncate'
import Avatar from 'components/Avatar'
import { Flex } from 'components/Box'
import ApolloErrorComponent from 'components/ApolloError'
import {
  Panel as StyledPanel,
  Heading,
  WordBreak,
  Content,
  BodyPanel,
  MainAction,
  SecondaryAction,
  NameWithAvatar,
  PanelFooter,
  InvitedEmail,
} from './shared'
import { Inviter } from './types'
import useSignOut from 'modules/user/useSignOut'
import { routes } from 'constants/routes'
import { ParsedError } from 'graphql/apollo/useMutation'
import { useShareRequestAccessMutation } from 'gqlTypes'
import { useToast } from 'containers/ToastManager'
import queryString from 'query-string'
import {
  HasPersonalIdentity,
  HasNoPersonalIdentity,
} from 'containers/HasPersonalIdentity'

import { UserInfo } from './AcceptMembershipPanel'

interface SwitchAccountOrAcceptMembershipPanelProps
  extends RouteComponentProps {
  invitedEmail?: string
  inviter?: Inviter
  documentName: string
  acceptShareMembership(): void
  acceptShareMembershipLoading?: boolean
  acceptShareMembershipError?: ParsedError
  loading?: boolean
  shareShortId: string
  userInfo: UserInfo
}

const SwitchAccountOrAcceptMembershipPanel = ({
  inviter,
  invitedEmail,
  documentName,
  acceptShareMembershipError,
  location,
  shareShortId,
  userInfo,
}: SwitchAccountOrAcceptMembershipPanelProps) => {
  const history = useHistory()
  const { showToast } = useToast()
  const query = queryString.parse(location.search)
  const { userEmail, userAvatar } = userInfo

  const signOut = useSignOut({
    pathname: routes.SHARE_ACCEPT_INVITE.create({
      query,
    }),
    search: location.search,
    state: {
      from: location,
      inviter,
      documentName,
    },
  })

  const [
    requestShareAccess,
    { loading: loadingRequestShareAccess },
  ] = useShareRequestAccessMutation({
    redirectErrors: true,
    variables: { shortId: shareShortId },
    onError: ({ message }) => {
      showToast(message, 'negative')
      /* Redirect home of the user already requested access before */
      if (message === "You've already requested access") {
        history.push(routes.ENTRY.create({}))
      }
    },
    onCompleted: () => {
      showToast('Request has been sent')
      /* Redirect home instead of showing the ShareAuthorizer NotFound */
      history.push(routes.ENTRY.create({}))
    },
  })

  return (
    <StyledPanel>
      <BodyPanel>
        {inviter && (
          <Flex justifyContent="center">
            <Avatar
              size="64px"
              src={inviter?.avatar?.large || undefined}
              name={inviter.name || '?' /** Name should always be there */}
            />
          </Flex>
        )}
        <Heading inviter={inviter} documentName={documentName} />
        <HasPersonalIdentity>
          <Content>
            <WordBreak>
              To access &ldquo;
              <strong>{truncate(documentName, { length: 100 })}</strong>&rdquo;
              now, switch to{' '}
              <InvitedEmail>
                <strong>{invitedEmail}</strong>
              </InvitedEmail>
              . Alternatively, you can request access using{' '}
              <NameWithAvatar name={userEmail} src={userAvatar} />
            </WordBreak>
          </Content>
        </HasPersonalIdentity>
        <HasNoPersonalIdentity>
          <Content>
            <WordBreak>
              To access &ldquo;
              <strong>{truncate(documentName, { length: 100 })}</strong>&rdquo;
              now, switch to{' '}
              <InvitedEmail>
                <strong>{invitedEmail}</strong>
              </InvitedEmail>
              . <br />
              You cannot accept this invitation using your SSO account. You’re
              currently signed in as{' '}
              <NameWithAvatar name={userEmail} src={userAvatar} />
            </WordBreak>
          </Content>
        </HasNoPersonalIdentity>
      </BodyPanel>
      <PanelFooter>
        {acceptShareMembershipError && (
          <ApolloErrorComponent
            error={acceptShareMembershipError}
            style={{ textAlign: 'left' }}
            my={4}
          />
        )}
        <Flex flexDirection="column" alignItems="center">
          <MainAction variant="primary-untinted" fill onClick={signOut}>
            Switch Account
          </MainAction>
          <HasPersonalIdentity>
            <SecondaryAction
              variant="secondary-untinted"
              fill
              onClick={requestShareAccess}
              loading={loadingRequestShareAccess}>
              Request access as {userEmail}
            </SecondaryAction>
          </HasPersonalIdentity>
        </Flex>
      </PanelFooter>
    </StyledPanel>
  )
}

export default withRouter(SwitchAccountOrAcceptMembershipPanel)
